/**
 * Behaviour flags — corrections of inconsistencies found in the original source
 * (docs/BUSINESS_RULES.md §11). Every flag defaults to `false`, i.e. the original
 * behaviour. An administrator may approve a correction in Settings; the change is audited.
 */
export interface BehaviorFlagDef {
  id: BehaviorFlagId;
  src: string;
  title: string;
  description: string;
}

export const BEHAVIOR_FLAG_IDS = [
  'persistDeliveryDetails',
  'keepEmployeeOnEdit',
  'useCustomEmployeeName',
  'fixReorderEmployee',
  'showDormantModules',
  'autoPrintDialog',
] as const;
export type BehaviorFlagId = (typeof BEHAVIOR_FLAG_IDS)[number];
export type BehaviorFlags = Record<BehaviorFlagId, boolean>;

export const BEHAVIOR_FLAGS: readonly BehaviorFlagDef[] = [
  {
    id: 'persistDeliveryDetails',
    src: 'SRC-01',
    title: 'حفظ ساعة وعنوان التوصيل',
    description:
      'في النسخة الأصلية تُجمع ساعة التوصيل والعنوان في نموذج الطلب لكن لا تُحفظ. عند التفعيل تُحفظ وتظهر في اللوجستيك والفواتير.',
  },
  {
    id: 'keepEmployeeOnEdit',
    src: 'SRC-02',
    title: 'الإبقاء على الموظف عند تعديل الطلب',
    description: 'في النسخة الأصلية يُمسح اسم الموظف عند تعديل الطلب. عند التفعيل يُعبأ الموظف الحالي تلقائياً.',
  },
  {
    id: 'useCustomEmployeeName',
    src: 'SRC-03',
    title: 'استخدام اسم الموظف المكتوب يدوياً',
    description: 'عند اختيار «أخرى» يُحفظ النص «أخرى» في الأصل. عند التفعيل يُحفظ الاسم المكتوب.',
  },
  {
    id: 'fixReorderEmployee',
    src: 'SRC-04',
    title: 'تصحيح الموظف في «إعادة الطلب»',
    description: 'في الأصل قد يُحفظ جوال العميل مكان اسم الموظف عند إعادة الطلب. عند التفعيل يبقى الموظف فارغاً.',
  },
  {
    id: 'showDormantModules',
    src: 'SRC-17',
    title: 'إظهار الوحدات غير الظاهرة',
    description: 'صفحات المواعيد والتخطيط الموسمي والجودة موجودة في الكود الأصلي لكنها غير ظاهرة في القائمة.',
  },
  {
    id: 'autoPrintDialog',
    src: 'SRC-26',
    title: 'فتح نافذة الطباعة تلقائياً',
    description:
      'نوافذ الطباعة في الأصل تحتوي على وسم سكربت معطوب فلا تُفتح نافذة الطباعة تلقائياً (يطبع المستخدم يدوياً). عند التفعيل تُفتح تلقائياً.',
  },
];

export const DEFAULT_BEHAVIOR_FLAGS: BehaviorFlags = {
  persistDeliveryDetails: false,
  keepEmployeeOnEdit: false,
  useCustomEmployeeName: false,
  fixReorderEmployee: false,
  showDormantModules: false,
  autoPrintDialog: false,
};

export function resolveBehaviorFlags(raw: unknown): BehaviorFlags {
  const out: BehaviorFlags = { ...DEFAULT_BEHAVIOR_FLAGS };
  if (raw && typeof raw === 'object') {
    for (const id of BEHAVIOR_FLAG_IDS) {
      const v = (raw as Record<string, unknown>)[id];
      if (typeof v === 'boolean') out[id] = v;
    }
  }
  return out;
}
